import React, { useState, useEffect } from 'react';
import { getTeams, bulkImportTeams, updateTeam, deleteTeam, getTeamUsage } from '../services/api';

function AdminTeams({ showFeedback }) {
    const [teams,       setTeams]       = useState([]);
    const [search,      setSearch]      = useState('');
    const [editId,      setEditId]      = useState(null);
    const [editData,    setEditData]    = useState({});
    const [bulkText,    setBulkText]    = useState('');
    const [section,     setSection]     = useState('list');

    useEffect(() => {
        loadTeams();
    }, []);

    const loadTeams = () => {
        getTeams().then(res => setTeams(res.data));
    };

    const handleEdit = (team) => {
        setEditId(team.id);
        setEditData({ name: team.name, short_name: team.short_name, conference: team.conference });
    };

    const handleSaveEdit = async (id) => {
        try {
            await updateTeam(id, editData);
            showFeedback('Team updated');
            setEditId(null);
            loadTeams();
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to update team', 'error');
        }
    };

    const handleDelete = async (team) => {
        let confirmMessage = `Delete ${team.name}? This can't be undone.`;
        try {
            const usageRes = await getTeamUsage(team.id);
            const names = usageRes.data.tournament_names;
            if (names.length > 0) {
                confirmMessage =
                    `${team.name} is currently used in ${names.length} tournament(s): ${names.join(', ')}.\n\n` +
                    `Deleting it will fail until it's removed from those tournaments' seeding first. Try anyway?`;
            }
        } catch (err) {
            // Non-fatal: fall back to the generic message and let the
            // actual delete attempt surface any real error, same as before.
        }

        const confirmed = window.confirm(confirmMessage);
        if (!confirmed) return;
        try {
            await deleteTeam(team.id);
            showFeedback(`${team.name} deleted`);
            loadTeams();
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to delete team', 'error');
        }
    };

    const handleBulkImport = async () => {
        try {
            const lines = bulkText.trim().split('\n').filter(l => l.trim());
            const teams = lines.map(line => {
                const parts = line.split(',').map(p => p.trim());
                return {
                    name:        parts[0] || '',
                    short_name:  parts[1] || '',
                    conference:  parts[2] || ''
                };
            }).filter(t => t.name);

            if (teams.length === 0) {
                showFeedback('No valid teams found. Format: Name, Short, Conference', 'error');
                return;
            }

            const res = await bulkImportTeams(teams);
            showFeedback(`Imported ${res.data.inserted} teams, skipped ${res.data.skipped}`);
            setBulkText('');
            loadTeams();
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to import teams', 'error');
        }
    };

    const filtered = teams.filter(t =>
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        (t.conference || '').toLowerCase().includes(search.toLowerCase())
    );

    const sections = [
        { id: 'list',   label: 'All Teams' },
        { id: 'import', label: 'Bulk Import' },
    ];

    return (
        <div>
            <div className="flex gap-2 mb-6">
                {sections.map(s => (
                    <button key={s.id} onClick={() => setSection(s.id)}
                        className={'px-4 py-2 rounded-lg text-sm font-bold transition-colors ' +
                            (section === s.id
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-800 text-gray-400 hover:text-white')}>
                        {s.label}
                    </button>
                ))}
            </div>

            {section === 'list' && (
                <div>
                    <div className="flex items-center gap-3 mb-4">
                        <input
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search teams or conferences..."
                            className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-orange-500"
                        />
                        <span className="text-gray-500 text-sm">{filtered.length} teams</span>
                    </div>

                    <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-800">
                                    <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">Name</th>
                                    <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">Short</th>
                                    <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">Conference</th>
                                    <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase tracking-wide">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((t, i) => (
                                    <tr key={t.id} className={'border-b border-gray-800 ' + (i % 2 === 0 ? '' : 'bg-gray-900/50')}>
                                        {editId === t.id ? (
                                            <>
                                                <td className="px-4 py-2">
                                                    <input value={editData.name} onChange={e => setEditData({...editData, name: e.target.value})}
                                                        className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-orange-500" />
                                                </td>
                                                <td className="px-4 py-2">
                                                    <input value={editData.short_name || ''} onChange={e => setEditData({...editData, short_name: e.target.value})}
                                                        className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-orange-500" />
                                                </td>
                                                <td className="px-4 py-2">
                                                    <input value={editData.conference || ''} onChange={e => setEditData({...editData, conference: e.target.value})}
                                                        className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-orange-500" />
                                                </td>
                                                <td className="px-4 py-2">
                                                    <div className="flex gap-2">
                                                        <button onClick={() => handleSaveEdit(t.id)}
                                                            className="bg-orange-500 hover:bg-orange-600 text-white text-xs px-2 py-1 rounded transition-colors">Save</button>
                                                        <button onClick={() => setEditId(null)}
                                                            className="bg-gray-700 hover:bg-gray-600 text-white text-xs px-2 py-1 rounded transition-colors">Cancel</button>
                                                    </div>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="px-4 py-3 text-white font-medium">{t.name}</td>
                                                <td className="px-4 py-3 text-gray-400">{t.short_name}</td>
                                                <td className="px-4 py-3 text-gray-400">{t.conference}</td>
                                                <td className="px-4 py-3">
                                                    <div className="flex gap-2">
                                                        <button onClick={() => handleEdit(t)}
                                                            className="bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white text-xs px-3 py-1 rounded transition-colors">
                                                            Edit
                                                        </button>
                                                        <button onClick={() => handleDelete(t)}
                                                            className="bg-gray-800 hover:bg-red-900 text-gray-400 hover:text-red-300 text-xs px-3 py-1 rounded transition-colors">
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {section === 'import' && (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
                    <h3 className="text-lg font-black text-white mb-2">Bulk import teams</h3>
                    <p className="text-gray-500 text-sm mb-4">
                        One team per line. Format: <span className="text-gray-300 font-mono">Name, Short, Conference</span>
                        <br />Example: <span className="text-gray-300 font-mono">Duke, DUKE, ACC</span>
                        <br />Existing teams will be updated, new teams will be added.
                    </p>

                    <div className="flex items-center gap-3 mb-4">
                        <label className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 hover:text-white text-sm font-bold px-4 py-2 rounded-lg cursor-pointer transition-colors">
                            Upload CSV
                            <input
                                type="file"
                                accept=".csv"
                                style={{ display: 'none' }}
                                onChange={e => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    const reader = new FileReader();
                                    reader.onload = ev => {
                                        const text = ev.target.result;
                                        const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.trim());
                                        const firstLine = lines[0]?.toLowerCase() || '';
                                        const dataLines = firstLine.includes('team_name') || firstLine.includes('name')
                                            ? lines.slice(1)
                                            : lines;
                                        setBulkText(dataLines.join('\n'));
                                    };
                                    reader.readAsText(file);
                                    e.target.value = '';
                                }}
                            />
                        </label>
                        <button
                            onClick={() => {
                                const csv = 'team_name,short_name,conference,seed\nDuke,DUKE,ACC,1\nNorth Carolina,UNC,ACC,2\nKentucky,UK,SEC,3';
                                const blob = new Blob([csv], { type: 'text/csv' });
                                const url  = URL.createObjectURL(blob);
                                const a    = document.createElement('a');
                                a.href     = url;
                                a.download = 'tournament_teams_template.csv';
                                a.click();
                                URL.revokeObjectURL(url);
                            }}
                            className="bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-400 hover:text-white text-sm font-bold px-4 py-2 rounded-lg transition-colors">
                            Download template
                        </button>
                    </div>

                    <textarea
                        value={bulkText}
                        onChange={e => setBulkText(e.target.value)}
                        rows={15}
                        placeholder={"Duke, DUKE, ACC\nNorth Carolina, UNC, ACC\nKentucky, UK, SEC"}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white text-sm font-mono focus:outline-none focus:border-orange-500 mb-4"
                    />
                    <div className="flex items-center justify-between">
                        <span className="text-gray-500 text-xs">
                            {bulkText.trim() ? bulkText.trim().split('\n').filter(l => l.trim()).length + ' teams ready to import' : ''}
                        </span>
                        <button onClick={handleBulkImport}
                            className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-2 rounded-lg transition-colors text-sm">
                            Import teams
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminTeams;